import { createTheme, type ThemeOptions } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    manage: Palette['primary'];
  }
  interface PaletteOptions {
    manage?: PaletteOptions['primary'];
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    manage: true;
  }
}

declare module '@mui/material/Fab' {
  interface FabPropsColorOverrides {
    manage: true;
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides {
    manage: true;
  }
}

declare module '@mui/material/IconButton' {
  interface IconButtonPropsColorOverrides {
    manage: true;
  }
}

// Color palette
export const colors = {
  // Primary colors
  primary: {
    main: '#ffda00', // yellow/gold
    light: '#ffe75c',
    dark: '#c7ab00',
    contrastText: '#1a1a1a',
  },
  // Secondary colors
  secondary: {
    main: '#00aaff', // blue
    light: '#4dc3ff',
    dark: '#0077cc',
    contrastText: '#ffffff',
  },
  // Background colors
  background: {
    dark: '#1a1a1a',
    darker: '#121212',
    card: '#2a2a2a',
    hover: '#333333',
    light: '#f5f5f5',
    paper: '#ffffff',
  },
  // Text colors
  text: {
    primary: '#ffffff',
    secondary: '#b0b0b0',
    muted: '#808080',
    dark: '#1a1a1a',
    light: '#ffffff',
  },
  // Status colors
  status: {
    live: '#ff4444',
    upcoming: '#ffda00',
    finished: '#4caf50',
    canceled: '#808080',
  },
  // Sport-specific colors
  sports: {
    football: '#4caf50',
    hockey: '#2196f3',
    basketball: '#ff9800',
    tennis: '#e91e63',
    volleyball: '#9c27b0',
    handball: '#00bcd4',
    padel: '#8bc34a',
    swimming: '#03a9f4',
    other: '#757575',
  },
  // Management controls (режим «Админ»)
  manage: {
    main: '#7c4dff',
    light: '#b47cff',
    dark: '#3f1dcb',
    contrastText: '#ffffff',
  },
  // Common
  border: '#3a3a3a',
  divider: '#3a3a3a',
};

// Common theme settings
const commonTheme: ThemeOptions = {
  // lg = 1024: единый порог десктопной раскладки (верхняя панель вместо нижней).
  breakpoints: {
    values: { xs: 0, sm: 600, md: 900, lg: 1024, xl: 1536 },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '1.75rem',
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '0.875rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.8125rem',
      lineHeight: 1.5,
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.4,
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 500,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          textTransform: 'none',
          fontWeight: 500,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          height: 56,
        },
      },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          minWidth: 'auto',
          padding: '6px 0 4px',
          backgroundColor: 'transparent',
          '&:hover, &:active, &.Mui-focusVisible': {
            backgroundColor: 'transparent',
          },
        },
        label: {
          fontSize: '0.7rem',
          '&.Mui-selected': {
            fontSize: '0.7rem',
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          padding: '8px 16px',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          height: 24,
          fontSize: '0.75rem',
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          background: 'inherit',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        outlined: {
          '&.MuiInputLabel-shrink': {
            zIndex: 1,
            padding: '0 6px',
            borderRadius: 4,
            background: 'inherit',
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: '16px',
        },
        input: {
          fontSize: '16px',
        },
      },
    },
  },
};

// Dark theme (default)
export const darkTheme = createTheme({
  ...commonTheme,
  palette: {
    mode: 'dark',
    primary: colors.primary,
    secondary: colors.secondary,
    manage: colors.manage,
    background: {
      default: colors.background.dark,
      paper: colors.background.card,
    },
    text: {
      primary: colors.text.primary,
      secondary: colors.text.secondary,
    },
    divider: colors.divider,
  },
});

// Light theme
export const lightTheme = createTheme({
  ...commonTheme,
  palette: {
    mode: 'light',
    primary: colors.primary,
    secondary: colors.secondary,
    manage: { ...colors.manage, main: '#651fff' },
    background: {
      default: colors.background.light,
      paper: colors.background.paper,
    },
    text: {
      primary: colors.text.dark,
      secondary: '#666666',
    },
    divider: '#e0e0e0',
  },
});

export type AppTheme = typeof darkTheme;
